/**
 * ProviderChangeSection - Section wrapper for provider change cards.
 *
 * Sorts by change type order and groups treatment requests by provider.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { ProviderChangeCard } from './ProviderChangeCard';

interface ProviderChangeSectionProps {
  providers: EntityChangeSchema[];
  treatmentRequests?: EntityChangeSchema[];
  tasks?: EntityChangeSchema[];
  matterId: string;
}

export const ProviderChangeSection: React.FC<ProviderChangeSectionProps> = ({
  providers,
  treatmentRequests = [],
  tasks = [],
  matterId,
}) => {
  const sorted = useMemo(
    () =>
      [...providers].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [providers],
  );

  // Group treatment requests by provider_id from snapshot data
  const trByProvider = useMemo(
    () =>
      treatmentRequests.reduce<Record<string, EntityChangeSchema[]>>(
        (map, tr) => {
          const snapshot =
            tr.change_type === 'deleted' ? tr.old_snapshot : tr.new_snapshot;
          const providerId = (snapshot?.data as Record<string, unknown>)
            ?.provider_id as string | undefined;
          if (providerId) {
            (map[providerId] ??= []).push(tr);
          }
          return map;
        },
        {},
      ),
    [treatmentRequests],
  );

  // Group tasks by provider medical_note_id from task snapshots
  const tasksByProvider = useMemo(
    () => {
      const treatmentRequestToProviderId = treatmentRequests.reduce<Record<string, string>>(
        (map, tr) => {
          const snapshot =
            tr.change_type === 'deleted' ? tr.old_snapshot : tr.new_snapshot;
          const data = (snapshot?.data ?? {}) as Record<string, unknown>;
          const treatmentRequestId = data.id as string | undefined;
          const providerId = data.provider_id as string | undefined;
          if (treatmentRequestId && providerId) {
            map[treatmentRequestId] = providerId;
          }
          return map;
        },
        {},
      );

      return tasks.reduce<Record<string, EntityChangeSchema[]>>((map, task) => {
        const oldData = ((task.old_snapshot?.data ?? {}) as Record<string, unknown>);
        const newData = ((task.new_snapshot?.data ?? {}) as Record<string, unknown>);

        const providerIds = new Set<string>();
        const directProviderIds = [
          oldData.medical_note_id as string | undefined,
          newData.medical_note_id as string | undefined,
        ].filter(Boolean) as string[];
        directProviderIds.forEach((id) => providerIds.add(id));

        const treatmentRequestIds = [
          oldData.treatment_record_request_id as string | undefined,
          newData.treatment_record_request_id as string | undefined,
        ].filter(Boolean) as string[];
        treatmentRequestIds.forEach((trId) => {
          const providerId = treatmentRequestToProviderId[trId];
          if (providerId) providerIds.add(providerId);
        });

        providerIds.forEach((providerId) => {
          (map[providerId] ??= []).push(task);
        });

        return map;
      }, {});
    },
    [tasks, treatmentRequests],
  );

  // Count only providers with real changes (not context-only backfills)
  const actionableCount = providers.filter(
    (p) => p.change_type !== 'ignored',
  ).length;

  if (providers.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Providers</h3>
        {actionableCount > 0 && (
          <Badge color="zinc" className="text-xs">
            {actionableCount}
          </Badge>
        )}
      </div>
      <div className="space-y-2">
        {sorted.map((provider) => (
          <ProviderChangeCard
            key={provider.entity_id}
            entity={provider}
            treatmentRequests={trByProvider[provider.entity_id]}
            relatedTasks={tasksByProvider[provider.entity_id]}
            matterId={matterId}
          />
        ))}
      </div>
    </div>
  );
};
